import { Icon } from '@chakra-ui/icons'
import Phyto from 'components/svg/Phyto'
import { find } from 'lodash'
import { useRouter } from 'next/router'
import { ReactElement, useMemo } from 'react'
import { BiChart, BiFirstAid, BiHome, BiSupport, BiTrendingUp, BiUser } from 'react-icons/bi'
import { BsBoxSeam, BsClipboardData, BsClipboardPlus, BsCollectionPlay, BsCreditCard, BsTruck } from 'react-icons/bs'
import { CgGym } from 'react-icons/cg'
import { FaClipboardList, FaShareAlt } from 'react-icons/fa'
import { HiOutlineClipboardCheck } from 'react-icons/hi'

export enum PatientAreaPage {
  SIGN_IN = '/me/signin',
  HOME = '/me/home',
  CONSULTATIONS = '/me/consultations',
  NEW_CONSULTATION = '/me/new-consultation',
  MEDICAL_INFO = '/me/medical-info',
  DOCUMENTS = '/me/documents',
  ORDERS = '/me/orders',
  SUBSCRIPTIONS = '/me/subscriptions',
  MY_CREDIT_CARDS = '/me/my-credit-cards',
  PHYTOTHERAPY = '/me/phytotherapy',
  PROGRAM_TIME = '/me/program-time',
  PROGRAM_MASTER = '/me/program-master',
  PREP_DOCUMENTS = '/me/prep-documents',
  PREP_REGISTRY = '/me/prep/[patientId]/newTreatment/[id]',
  PREP_FOLLOW_UP = '/me/prep',
  HELP = '/me/help',
  ACCOUNT = '/me/account',
  WEIGHT_DASHBOARD = '/me/weight-dashboard',
  OMENS_FIT = '/me/weight-logged-page',
  WEIGHT_DOCUMENTS = '/me/weight-documents',
  ISHA_DOCUMENTS = '/me/isha-documents',
  MEDLEVE_DOCUMENTS = '/me/medleve-documents',
  SHARE_MY_COUPON = '/me/my-coupon',
  MY_EVOLUTION = '/me/my-evolution',
  GLP1 = '/me/glp1-daily-support'
}

export const NEW_CONSULTATION_TYPE_PATH = `${PatientAreaPage.NEW_CONSULTATION}/[type]`
export const NEW_CONSULTATION_STEP_PATH = `${NEW_CONSULTATION_TYPE_PATH}/[step]`

export const PHYTOTHERAPY_CHECKOUT_PATH = `${PatientAreaPage.PHYTOTHERAPY}/checkout/[step]`
export const GLP1_CHECKOUT_PATH = `${PatientAreaPage.GLP1}/checkout/[step]`

export const patientAreaPageTitleMap: Record<PatientAreaPage, string> = {
  [PatientAreaPage.SIGN_IN]: 'Login',
  [PatientAreaPage.HOME]: 'Página inicial',
  [PatientAreaPage.CONSULTATIONS]: 'Consultas',
  [PatientAreaPage.NEW_CONSULTATION]: 'Nova consulta',
  [PatientAreaPage.MEDICAL_INFO]: 'Minhas receitas/guias',
  [PatientAreaPage.DOCUMENTS]: 'Meus exames',
  [PatientAreaPage.ORDERS]: 'Meus pedidos',
  [PatientAreaPage.SUBSCRIPTIONS]: 'Minhas assinaturas',
  [PatientAreaPage.MY_CREDIT_CARDS]: 'Meus cartões',
  [PatientAreaPage.PHYTOTHERAPY]: 'Suplementos',
  [PatientAreaPage.PROGRAM_TIME]: 'Programa CONTROL',
  [PatientAreaPage.PROGRAM_MASTER]: 'Programa MASTER',
  [PatientAreaPage.PREP_DOCUMENTS]: 'Prevenção HIV (PrEP)',
  [PatientAreaPage.PREP_REGISTRY]: 'Prevenção HIV (PrEP)',
  [PatientAreaPage.PREP_FOLLOW_UP]: 'Prevenção HIV (PrEP)',
  [PatientAreaPage.HELP]: 'Assistência',
  [PatientAreaPage.ACCOUNT]: 'Minha conta',
  [PatientAreaPage.WEIGHT_DASHBOARD]: 'Painel de medidas',
  [PatientAreaPage.OMENS_FIT]: 'Omens+fit',
  [PatientAreaPage.WEIGHT_DOCUMENTS]: 'Documentos Omens+fit',
  [PatientAreaPage.ISHA_DOCUMENTS]: 'Documentos Isha',
  [PatientAreaPage.MEDLEVE_DOCUMENTS]: 'Documentos Medleve',
  [PatientAreaPage.SHARE_MY_COUPON]: 'Compartilhe seu cupom',
  [PatientAreaPage.MY_EVOLUTION]: 'Minha Evolução',
  [PatientAreaPage.GLP1]: 'GLP-1 Daily Support'
}

const patientAreaDescriptionMap: Partial<Record<PatientAreaPage, string>> = {
  [PatientAreaPage.PROGRAM_TIME]: 'Terapia intensiva de controle da ejaculação',
  [PatientAreaPage.PROGRAM_MASTER]: 'Terapia de controle da ereção',
  [PatientAreaPage.HELP]: 'Nossos especialistas acompanham você de segunda a sexta-feira durante todo o programa',
  [PatientAreaPage.MY_EVOLUTION]: 'Acompanhe sua evolução através dos questionários respondidos'
}

export const patientAreaIconMap: Record<PatientAreaPage, ReactElement | null> = {
  [PatientAreaPage.SIGN_IN]: null,
  [PatientAreaPage.HOME]: <Icon as={BiHome} fontSize="xl" />,
  [PatientAreaPage.CONSULTATIONS]: <Icon as={FaClipboardList} fontSize="xl" />,
  [PatientAreaPage.NEW_CONSULTATION]: <Icon as={BsClipboardPlus} fontSize="xl" />,
  [PatientAreaPage.MEDICAL_INFO]: <Icon as={BsClipboardData} fontSize="xl" />,
  [PatientAreaPage.DOCUMENTS]: <Icon as={BiFirstAid} fontSize="xl" />,
  [PatientAreaPage.ORDERS]: <Icon as={BsBoxSeam} fontSize="xl" />,
  [PatientAreaPage.SUBSCRIPTIONS]: <Icon as={BsTruck} fontSize="xl" />,
  [PatientAreaPage.MY_CREDIT_CARDS]: <Icon as={BsCreditCard} fontSize="xl" />,
  [PatientAreaPage.PHYTOTHERAPY]: <Phyto fontSize="xl" />,
  [PatientAreaPage.PROGRAM_TIME]: <Icon as={BsCollectionPlay} fontSize="xl" />,
  [PatientAreaPage.PROGRAM_MASTER]: <Icon as={BsCollectionPlay} fontSize="xl" />,
  [PatientAreaPage.PREP_DOCUMENTS]: <Icon as={HiOutlineClipboardCheck} fontSize="xl" />,
  [PatientAreaPage.PREP_REGISTRY]: <Icon as={HiOutlineClipboardCheck} fontSize="xl" />,
  [PatientAreaPage.PREP_FOLLOW_UP]: <Icon as={HiOutlineClipboardCheck} fontSize="xl" />,
  [PatientAreaPage.HELP]: <Icon as={BiSupport} fontSize="xl" />,
  [PatientAreaPage.ACCOUNT]: <Icon as={BiUser} fontSize="xl" />,
  [PatientAreaPage.WEIGHT_DASHBOARD]: <Icon as={BiChart} fontSize="xl" />,
  [PatientAreaPage.OMENS_FIT]: <Icon as={CgGym} fontSize="xl" />,
  [PatientAreaPage.WEIGHT_DOCUMENTS]: <Icon as={BsClipboardData} fontSize="xl" />,
  [PatientAreaPage.ISHA_DOCUMENTS]: <Icon as={BsClipboardData} fontSize="xl" />,
  [PatientAreaPage.MEDLEVE_DOCUMENTS]: <Icon as={BsClipboardData} fontSize="xl" />,
  [PatientAreaPage.SHARE_MY_COUPON]: <Icon as={FaShareAlt} fontSize="xl" />,
  [PatientAreaPage.MY_EVOLUTION]: <Icon as={BiTrendingUp} fontSize="xl" />,
  [PatientAreaPage.GLP1]: <Icon as={BiTrendingUp} fontSize="xl" />
}

export const usePatientAreaPageTitle = () => {
  const router = useRouter()

  return useMemo(() => {
    const currentPage = router.pathname as PatientAreaPage

    if (currentPage.startsWith('/me/questionnaire/')) return 'Questionário'
    return find(patientAreaPageTitleMap, (_, page) => currentPage.includes(page)) || null
  }, [router.pathname])
}

export const usePatientAreaPageDescription = () => {
  const router = useRouter()

  return useMemo(() => {
    const currentPage = router.pathname as PatientAreaPage

    return find(patientAreaDescriptionMap, (_, page) => currentPage.includes(page)) || null
  }, [router.pathname])
}
